import useScreenSize, { useCustomScreenSize } from "hoc/useScreenSize";
import { DEFAULT_CATEGORY_BANNER } from "modules/site-structure/common/make-model-year-utils";
import Head from "next/head";
import Image from "next/legacy/image";
import { FC, useState, useEffect } from "react";
import { getRelativeImagePath } from "../../../util/helper/getRelativeImagePath";
import styles from "./responsiveBanner.module.scss";
export interface IScreenData {
    url: string;
    height: number;
    alt: string;
}

export interface IResponsiveMediaData {
    desktop: IScreenData;
    mobile?: IScreenData;
    tablet: IScreenData;
    squareImage?: IScreenData;
}
export interface IResponsiveMedia {
    responsiveMedia?: IResponsiveMediaData | null;
}

interface IProps extends IResponsiveMedia {
    videoClassName?: string;
    imageClassName?: string;
    maintainAspectRatio?: boolean;
}

const ResponsiveBanner: FC<IProps> = ({
  responsiveMedia,
  videoClassName = "",
  imageClassName = "",
  maintainAspectRatio,
}) => {
  if (!responsiveMedia) {
    return null;
  }

  const videoMedia = (Object.keys(responsiveMedia) as Array<keyof typeof responsiveMedia>).reduce((acc, curr): IResponsiveMediaData => {
    if (responsiveMedia[curr].url.split(".").pop() === "mp4") {
      return ({
        ...acc,
        [curr]: responsiveMedia[curr],
      } as IResponsiveMediaData);
    }
    return acc;
  }, ({} as IResponsiveMediaData));

  const isVideoMediaPresent = videoMedia && Object.keys(videoMedia).length > 0;

  const [isMobile, isTablet, isDesktop] = useScreenSize();

  const smallMobile = useCustomScreenSize({
    maxWidth: 424,
  });

  const smallTablet = useCustomScreenSize({
    minWidth: 425,
    maxWidth: 991,
  });

  let videoSrc = "";
  let videoHeight = 0;

  // let imageSrc = "";
  let alt = "";

  const getMedia = (key: keyof IResponsiveMediaData) => {
    if (isVideoMediaPresent) {
      videoSrc = videoMedia[key].url;
      videoHeight = videoMedia[key].height;
    }
    alt = responsiveMedia[key]?.alt;
  };

  if (isMobile) {
    getMedia("mobile");
  } else if (isTablet) {
    getMedia("tablet");
  } else if (isDesktop) {
    getMedia("desktop");
  }

  if (isVideoMediaPresent) {
    return (
      <video
        autoPlay
        loop
        controls={false}
        muted
        style={{ objectFit: "contain", height: videoHeight, width: "100%" }}
        className={`banner_video ${videoClassName}`}
      >
        <source src={getRelativeImagePath(`/cms/${videoSrc}`)} type="video/mp4" />
      </video>
    );
  }

  const ImageComponent = ({
    fallbackImage,
  }: any) => {
    const [isError, sethasError] = useState(false);
    useEffect(() => {
      sethasError(false);
    }, [responsiveMedia.squareImage?.url || responsiveMedia.mobile?.url || responsiveMedia.tablet.url || responsiveMedia.desktop.url]);

    const showSquareImage = "squareImage" in responsiveMedia && responsiveMedia.squareImage?.url;
    let headTagImageUrl = showSquareImage ? responsiveMedia.squareImage?.url : responsiveMedia.mobile.url;

    if (isDesktop) {
      headTagImageUrl = responsiveMedia.desktop.url;
    } else if (smallTablet) {
      headTagImageUrl = responsiveMedia.tablet.url;
    }

    if (maintainAspectRatio) {
      return (
        <div>
          <Image
            unoptimized
            src={!isError ? getRelativeImagePath(`/cms/${responsiveMedia.desktop.url}`) : fallbackImage || DEFAULT_CATEGORY_BANNER}
            alt={alt}
            title={alt}
            width={1140}
            height={230}
            objectFit="contain"
            onError={() => sethasError(true)}
          />
        </div>
      );
    }

    return (
      <>
        <Head>
          <link rel="preload" as="image" href={getRelativeImagePath(`/cms/${headTagImageUrl}`)} />
        </Head>
        {smallMobile && (
          <div className={styles.mobile_view}>
            {showSquareImage && (
              <Image
                layout="responsive"
                unoptimized
                src={!isError ? getRelativeImagePath(`/cms/${responsiveMedia.squareImage?.url}`) : fallbackImage || DEFAULT_CATEGORY_BANNER}
                alt={alt}
                height={1}
                width={1}
                onError={() => sethasError(true)}
              />
            )}

            {!showSquareImage && (
              <img
                className={`banner_video ${imageClassName}`}
                onError={() => sethasError(true)}
                src={!isError ? getRelativeImagePath(`/cms/${responsiveMedia.mobile.url}`) : fallbackImage || DEFAULT_CATEGORY_BANNER}
                style={{ objectFit: "contain", height: responsiveMedia.mobile.height, width: "100%" }}
                alt={alt}
              />
            )}

          </div>
        )}

        {
          smallTablet && (
            <div>
              <Image
                layout="responsive"
                unoptimized
                src={!isError ? getRelativeImagePath(`/cms/${responsiveMedia.tablet.url}`) : fallbackImage || DEFAULT_CATEGORY_BANNER}
                alt={alt}
                height={330}
                width={643}
                onError={() => sethasError(true)}
              />
            </div>
          )
        }

        {
          isDesktop && (
            <div className={styles.desktop_view}>
              <Image
                layout="responsive"
                unoptimized
                src={!isError ? getRelativeImagePath(`/cms/${responsiveMedia.desktop.url}`) : fallbackImage || DEFAULT_CATEGORY_BANNER}
                alt={alt}
                height={250}
                width={1140}
                onError={() => sethasError(true)}
              />
            </div>
          )
        }

        {/* <img
        className={`banner_video ${imageClassName}`}

        src={!isError ? src : fallbackImage || DEFAULT_CATEGORY_BANNER}
        style={{ objectFit: "contain", height: "calc(20 * var(--safari-root-font, 10px))", width: "100%" }}
        alt={imageAlt}
      /> */}
      </>
    );
  };

  return (
    <ImageComponent
      fallbackImage={DEFAULT_CATEGORY_BANNER}
      imageClassName={imageClassName}
    />
  );
};

export { ResponsiveBanner };
